import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Mail, Send, Settings, FileText, History, RefreshCw, Loader2,
  Eye, Users, CheckCircle2, AlertCircle, Plus, Sparkles, BarChart3,
  Code2, Monitor, ChevronDown, Zap
} from 'lucide-react';

const VARIABLES = [
  { label: '{{nombre}}', desc: 'Nombre del cliente' },
  { label: '{{restaurante}}', desc: 'Nombre del restaurante' },
  { label: '{{email_restaurante}}', desc: 'Email del restaurante' },
  { label: '{{link_reservacion}}', desc: 'Link para reservar' },
  { label: '{{fecha}}', desc: 'Fecha de la reservación' },
  { label: '{{hora}}', desc: 'Hora de la reservación' },
  { label: '{{personas}}', desc: 'Número de personas' },
  { label: '{{telefono}}', desc: 'Teléfono del restaurante' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_HTML = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><style>
body { font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f9fafb; }
.container { background: #fff; border-radius: 8px; padding: 32px; margin: 20px; }
.header { text-align: center; padding-bottom: 24px; border-bottom: 2px solid #f97316; }
.restaurant-name { color: #f97316; font-size: 22px; font-weight: bold; }
.content { padding: 24px 0; color: #374151; line-height: 1.6; }
.footer { text-align: center; color: #9ca3af; font-size: 12px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
.btn { display: inline-block; background: #f97316; color: #fff; padding: 12px 24px; border-radius: 6px; text-decoration: none; font-weight: bold; }
</style></head>
<body>
<div class="container">
  <div class="header"><div class="restaurant-name">{{restaurante}}</div></div>
  <div class="content">
    <h2>Hola {{nombre}},</h2>
    <p>Escribe tu mensaje aquí...</p>
    <br>
    <p><a class="btn" href="{{link_reservacion}}">Hacer una reservación</a></p>
  </div>
  <div class="footer">{{restaurante}} · {{email_restaurante}}</div>
</div>
</body>
</html>`;

export default function EmailMarketing() {
  const [tab, setTab] = useState('campaign');
  const [configLoading, setConfigLoading] = useState(false);
  const [myConfig, setMyConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ sender_name: '', sender_email: '', reply_to: '', footer_text: '' });
  const [savingConfig, setSavingConfig] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [campaignForm, setCampaignForm] = useState({ subject: '', html: DEFAULT_HTML, to_emails: '' });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [editorMode, setEditorMode] = useState('split');
  const [varMenuOpen, setVarMenuOpen] = useState(false);
  const textareaRef = useRef(null);

  const token = localStorage.getItem('restaurant_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => { loadMyConfig(); loadTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMyConfig() {
    setConfigLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/my-config`, { headers });
      if (r.ok) {
        const d = await r.json();
        setMyConfig(d);
        if (d.config) {
          setConfigForm({
            sender_name: d.config.sender_name || '',
            sender_email: d.config.sender_email || '',
            reply_to: d.config.reply_to || '',
            footer_text: d.config.footer_text || '',
          });
        }
      }
    } catch {}
    finally { setConfigLoading(false); }
  }

  async function saveMyConfig() {
    if (!configForm.sender_name || !configForm.sender_email) {
      toast.error('Nombre y email del remitente son requeridos');
      return;
    }
    setSavingConfig(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/my-config`, {
        method: 'POST', headers, body: JSON.stringify(configForm),
      });
      const d = await r.json();
      if (d.ok) { toast.success('Configuración guardada'); loadMyConfig(); }
      else toast.error(d.detail || 'Error al guardar');
    } catch { toast.error('Error de conexión'); }
    finally { setSavingConfig(false); }
  }

  async function loadTemplates() {
    setTemplatesLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/templates`, { headers });
      if (r.ok) { const d = await r.json(); setTemplates(d.templates || []); }
    } catch {}
    finally { setTemplatesLoading(false); }
  }

  async function sendCampaign() {
    const emails = campaignForm.to_emails.split(/[\n,;]/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error('Agrega al menos un destinatario'); return; }
    if (!campaignForm.subject.trim()) { toast.error('El asunto es requerido'); return; }
    if (!campaignForm.html.trim()) { toast.error('El contenido es requerido'); return; }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/send-campaign`, {
        method: 'POST', headers,
        body: JSON.stringify({ subject: campaignForm.subject, html: campaignForm.html, to_emails: emails }),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(d.message);
        setCampaignForm(f => ({ ...f, to_emails: '', subject: '' }));
        loadCampaigns();
      } else toast.error(d.detail || 'Error al enviar');
    } catch { toast.error('Error de conexión'); }
    finally { setSending(false); }
  }

  async function loadCampaigns() {
    setCampaignsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/campaigns`, { headers });
      if (r.ok) { const d = await r.json(); setCampaigns(d.campaigns || []); }
    } catch {}
    finally { setCampaignsLoading(false); }
  }

  function applyTemplate(t) {
    setSelectedTemplate(t);
    setCampaignForm(f => ({ ...f, subject: t.subject, html: t.html }));
    toast.success(`Plantilla "${t.name}" aplicada`);
  }

  function insertVariable(variable) {
    const el = textareaRef.current;
    if (!el) { setCampaignForm(f => ({ ...f, html: f.html + variable })); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newHtml = campaignForm.html.slice(0, start) + variable + campaignForm.html.slice(end);
    setCampaignForm(f => ({ ...f, html: newHtml }));
    setTimeout(() => { el.selectionStart = el.selectionEnd = start + variable.length; el.focus(); }, 0);
    setVarMenuOpen(false);
  }

  const sendpulseActive = myConfig?.sendpulse_active;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Mail className="h-6 w-6 text-orange-500" />Email Marketing
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Envía correos transaccionales y campañas de marketing a tus clientes.
        </p>
      </div>

      {!sendpulseActive && !configLoading && (
        <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Email no configurado</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              El servidor de email aún no está activado en esta plataforma. Contacta al administrador de OkReserva para habilitarlo.
            </p>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => {
        setTab(v);
        if (v === 'history') loadCampaigns();
      }}>
        <TabsList>
          <TabsTrigger value="campaign"><Send className="h-4 w-4 mr-1.5" />Nueva Campaña</TabsTrigger>
          <TabsTrigger value="templates"><FileText className="h-4 w-4 mr-1.5" />Plantillas</TabsTrigger>
          <TabsTrigger value="history"><History className="h-4 w-4 mr-1.5" />Historial</TabsTrigger>
          <TabsTrigger value="config"><Settings className="h-4 w-4 mr-1.5" />Configuración</TabsTrigger>
        </TabsList>

        {/* ── CAMPAIGN ── */}
        <TabsContent value="campaign" className="space-y-4 mt-4">
          {templates.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-orange-500" />Usar plantilla
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 flex-wrap">
                  {templates.map(t => (
                    <Button key={t.id} variant={selectedTemplate?.id === t.id ? 'default' : 'outline'} size="sm"
                      className={selectedTemplate?.id === t.id ? 'bg-orange-500 hover:bg-orange-600' : ''}
                      onClick={() => applyTemplate(t)}>
                      {t.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Componer mensaje</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Asunto del email</Label>
                <Input value={campaignForm.subject} onChange={e => setCampaignForm(f => ({ ...f, subject: e.target.value }))}
                  placeholder="¡Nueva carta de verano disponible!" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="flex items-center gap-1.5"><Code2 className="h-3.5 w-3.5" />Contenido HTML</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex border rounded-lg overflow-hidden text-xs">
                      {[{ k:'code', l:'Código', I: Code2 }, { k:'split', l:'Dividido', I: Monitor }, { k:'preview', l:'Preview', I: Eye }].map(({ k, l, I }) => (
                        <button key={k} onClick={() => setEditorMode(k)}
                          className={`px-2.5 py-1.5 flex items-center gap-1 transition-colors ${editorMode===k ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-50 text-gray-600'}`}>
                          <I className="h-3 w-3" />{l}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <button onClick={() => setVarMenuOpen(v => !v)}
                        className="flex items-center gap-1 text-xs border rounded-lg px-2.5 py-1.5 bg-white hover:bg-gray-50 text-gray-600">
                        <Zap className="h-3 w-3 text-orange-500" />Variable<ChevronDown className="h-3 w-3" />
                      </button>
                      {varMenuOpen && (
                        <div className="absolute right-0 top-8 z-50 bg-white border rounded-xl shadow-xl w-56 py-1">
                          {VARIABLES.map(v => (
                            <button key={v.label} onClick={() => insertVariable(v.label)}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 flex items-center justify-between">
                              <span className="font-mono text-orange-600">{v.label}</span>
                              <span className="text-gray-400 text-[10px]">{v.desc}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`border rounded-xl overflow-hidden ${editorMode==='split' ? 'grid grid-cols-2' : ''}`} style={{ height: 340 }}>
                  {(editorMode === 'code' || editorMode === 'split') && (
                    <textarea
                      ref={textareaRef}
                      value={campaignForm.html}
                      onChange={e => setCampaignForm(f => ({ ...f, html: e.target.value }))}
                      className="w-full h-full resize-none outline-none font-mono text-xs p-3 bg-gray-950 text-green-400 leading-relaxed"
                      spellCheck={false}
                      style={{ borderRight: editorMode==='split' ? '1px solid #374151' : 'none' }}
                    />
                  )}
                  {(editorMode === 'preview' || editorMode === 'split') && (
                    <iframe
                      srcDoc={campaignForm.html}
                      title="preview"
                      className="w-full h-full bg-white"
                      sandbox="allow-same-origin"
                    />
                  )}
                </div>
                <p className="text-xs text-gray-400">Usa el menú <span className="font-medium text-orange-500">Variable</span> para insertar campos dinámicos en el cursor</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Destinatarios</Label>
                <Textarea value={campaignForm.to_emails} onChange={e => setCampaignForm(f => ({ ...f, to_emails: e.target.value }))}
                  rows={4} placeholder="cliente1@email.com&#10;cliente2@email.com&#10;cliente3@email.com" />
                <p className="text-xs text-gray-400">
                  {campaignForm.to_emails.split(/[\n,;]/).filter(e => e.trim()).length} destinatario(s) · Máximo 500 por envío
                </p>
              </div>
              {myConfig?.config && (
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 rounded px-3 py-2">
                  <Mail className="h-3.5 w-3.5" />
                  Enviando desde: <span className="font-medium">{myConfig.config.sender_name} &lt;{myConfig.config.sender_email}&gt;</span>
                </div>
              )}
              <Button onClick={sendCampaign} disabled={sending || !sendpulseActive}
                className="bg-orange-500 hover:bg-orange-600">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Enviar Campaña
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TEMPLATES ── */}
        <TabsContent value="templates" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{templates.length} plantilla(s) disponible(s)</p>
            <Button variant="outline" size="sm" onClick={loadTemplates} disabled={templatesLoading}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${templatesLoading ? 'animate-spin' : ''}`} />Actualizar
            </Button>
          </div>
          {templatesLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
          ) : templates.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay plantillas disponibles.</p>
              <p className="text-sm text-gray-400 mt-1">El administrador de OkReserva puede crear plantillas globales.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(t => (
                <Card key={t.id} className={selectedTemplate?.id === t.id ? 'border-orange-400 shadow-md' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{t.name}</CardTitle>
                        <CardDescription className="mt-0.5">{t.subject}</CardDescription>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">{t.category}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setPreviewHtml(t.html); setShowPreview(true); }}>
                        <Eye className="h-3.5 w-3.5 mr-1" />Preview
                      </Button>
                      <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => { applyTemplate(t); setTab('campaign'); }}>
                        Usar plantilla
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── HISTORY ── */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{campaigns.length} campaña(s) enviada(s)</p>
            <Button variant="outline" size="sm" onClick={loadCampaigns} disabled={campaignsLoading}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${campaignsLoading ? 'animate-spin' : ''}`} />Actualizar
            </Button>
          </div>
          {campaignsLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
          ) : campaigns.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <History className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Sin historial de campañas.</p>
              <p className="text-sm text-gray-400">Las campañas enviadas aparecerán aquí.</p>
            </CardContent></Card>
          ) : (
            <Card>
              <div className="divide-y dark:divide-gray-700">
                {campaigns.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                        <Send className="h-4 w-4 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.subject}</p>
                        <p className="text-xs text-gray-500">{c.to_count} destinatarios · {c.sender_email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs font-medium ${c.status === 'sent' ? 'text-green-600' : 'text-red-500'}`}>
                        {c.status === 'sent' ? '✓ Enviada' : 'Error'}
                      </p>
                      <p className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString('es-MX')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>

        {/* ── CONFIG ── */}
        <TabsContent value="config" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-orange-500" />Configuración de correo</CardTitle>
              <CardDescription>Personaliza el nombre y email desde el que envías tus comunicaciones.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {myConfig && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${sendpulseActive ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                  {sendpulseActive ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  {sendpulseActive
                    ? `Email activo · Remitente global: ${myConfig.global_sender_name} <${myConfig.global_sender_email}>`
                    : 'Email no activado por el administrador'}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre del remitente</Label>
                  <Input value={configForm.sender_name} onChange={e => setConfigForm(f => ({ ...f, sender_name: e.target.value }))}
                    placeholder="Mi Restaurante" />
                </div>
                <div className="space-y-2">
                  <Label>Email del remitente</Label>
                  <Input type="email" value={configForm.sender_email} onChange={e => setConfigForm(f => ({ ...f, sender_email: e.target.value }))}
                    placeholder="hola@mirestaurante.com" />
                </div>
                <div className="space-y-2">
                  <Label>Reply-To (opcional)</Label>
                  <Input type="email" value={configForm.reply_to} onChange={e => setConfigForm(f => ({ ...f, reply_to: e.target.value }))}
                    placeholder="reservas@mirestaurante.com" />
                </div>
                <div className="space-y-2">
                  <Label>Pie de página</Label>
                  <Input value={configForm.footer_text} onChange={e => setConfigForm(f => ({ ...f, footer_text: e.target.value }))}
                    placeholder="Mi Restaurante · Calle Principal 123" />
                </div>
              </div>
              <Button onClick={saveMyConfig} disabled={savingConfig} className="bg-orange-500 hover:bg-orange-600">
                {savingConfig ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Guardar Configuración
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── PREVIEW DIALOG ── */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader><DialogTitle>Vista Previa del Email</DialogTitle></DialogHeader>
          <div className="border rounded-lg overflow-hidden h-[55vh]">
            <iframe srcDoc={previewHtml} title="preview" className="w-full h-full" sandbox="allow-same-origin" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
